import React, { useRef } from 'react';
import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import { LazyRender } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import dynamic from 'next/dynamic';

import { adaptPublicAttributesToUI } from 'src/adapters/storeToUI/adaptPublicAttributesToUI';
import { BasketErrorNotification } from 'src/components/atoms/BasketErrorNotification/BasketErrorNotification';
import { OfferLabel } from 'src/components/atoms/OfferLabel/OfferLabel';
import { BasketCTAButtonContainer } from 'src/components/molecules/BasketCTAButton/BasketCTAButtonContainer';
import { MAIN_PAGE_CTA_ID } from 'src/components/molecules/BasketCTAButton/elementIDs';
import { BreadcrumbContainer } from 'src/components/molecules/Breadcrumbs/containers/BreadcrumbsContainer';
import { DeliverySummary } from 'src/components/molecules/DeliverySummary/DeliverySummary';
import { ExpandablePanel } from 'src/components/molecules/ExpandablePanel/ExpandablePanel';
import { MadeToOrder } from 'src/components/molecules/MadeToOrder/MadeToOrder';
import { MembershipBannerSection } from 'src/components/molecules/MembershipBannerSection/MembershipBannerSection';
import { PartnerMaxiModuleWithTracking } from 'src/components/molecules/PartnerMaxiModule/components/PartnerMaxiModuleWithTracking';
import { PartnerMiniModule } from 'src/components/molecules/PartnerMiniModule/PartnerMiniModule';
import { ProductFeaturesWithTracking } from 'src/components/molecules/ProductFeatures/components/ProductFeaturesWithTracking';
import { getTopProductFeatures } from 'src/components/molecules/ProductFeatures/utils/getTopProductFeatures';
import { ProductPriceAndRatingWithTracking } from 'src/components/molecules/ProductPriceAndRating/ProductPriceAndRatingWithTracking';
import { ProductRatingMaxiSummary } from 'src/components/molecules/ProductRatingMaxiSummary/ProductRatingMaxiSummary';
import { PublicAttributes } from 'src/components/molecules/PublicAttributes/PublicAttributes';
import { StickySection } from 'src/components/molecules/StickySection/components/StickySection';
import { TrustCarouselWithTracking } from 'src/components/molecules/TrustCarousel/components/TrustCarouselWithTracking';
import { LinkCarouselContainer } from 'src/components/organisms/LinkCarousel/containers/LinkCarouselContainer';
import { ProductDescriptionContainer } from 'src/components/organisms/ProductDescription/containers/ProductDescriptionContainer';
import { ProductImagesCarouselContainer } from 'src/components/organisms/ProductImagesCarousel/ProductImagesCarouselContainer';
import { ProductImagesModalContainer } from 'src/components/organisms/ProductImagesModal/ProductImagesModalContainer';
import { ProductOptionsContainer } from 'src/components/organisms/ProductOptions/ProductOptionsContainer';
import { ProductReviewsContainer } from 'src/components/organisms/ProductReviews/containers/ProductReviewsContainer';
import { RecommendedProductsCarouselContainer } from 'src/components/organisms/RecommendedProductsCarousel/containers/RecommendedProductsCarouselContainer';
import { REVIEWS_URL_FRAGMENT_ID } from 'src/fragmentIDs';
import { isMadeToOrder } from 'src/utils/inventoryStatus';
import { useEditProductOptions } from '../hooks/useEditProductOptions';
import { PRODUCT_DETAILS_HEADING } from './copy';
import { PARTNER_MODULE_ID } from './elementIds';
import { useStandardLayout } from './useStandardLayout';
import * as styles from './StandardLayout.styles';

const DeliveryDrawer = dynamic(() =>
  import(
    /* webpackChunkName: "delivery_drawer" */ 'src/components/organisms/DeliveryDrawer/DeliveryDrawer'
  ).then((mod) => mod.DeliveryDrawer),
);

const ProductOptionsDrawerContainer = dynamic(() =>
  import(
    /* webpackChunkName: "product_options_drawer" */ 'src/components/organisms/ProductOptions/ProductOptionsDrawerContainer'
  ).then((mod) => mod.ProductOptionsDrawerContainer),
);

const RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN = '200px';

export const StandardLayout = () => {
  const {
    deliveryOptionsLoaded,
    offerLabel,
    openContactPartnerModal,
    options,
    optionsCostRange,
    pageLiveRegionText,
    partnerHref,
    partnerRating,
    partnerReviewCount,
    product,
    reviews,
  } = useStandardLayout();
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const { allowsGiftWrap, specialFeatures } = product;
  const optionsAddCost =
    options.length > 0 && specialFeatures.personalisable && optionsCostRange.minRequired > 0;
  const minimumOptionsCost = optionsAddCost ? optionsCostRange.minRequired : 0;
  const priceIsMinimum = optionsCostRange.maxOptional + optionsCostRange.maxRequired > 0;
  const offerLabelAllowedForProduct = !offerLabel?.showOnlyForDiscountedProducts || product.onSale;
  const productFeatures = getTopProductFeatures({
    allowsGiftWrap,
    productRating: reviews.averageProductRating,
    specialFeatures,
  });

  useEditProductOptions();

  return (
    <>
      <div css={visuallyHidden} role="status">
        {pageLiveRegionText}
      </div>
      <div css={styles.wrapper}>
        <div css={[styles.maxWidthContainer, styles.breadcrumbsContainer]}>
          <BreadcrumbContainer />
        </div>

        {/* start grid containing a column that spans across rows */}
        <div css={[styles.maxWidthContainer, styles.grid, styles.largeSpacingBottom]}>
          <h1 css={[styles.narrowColumn, styles.productTitle]}>{product.title}</h1>
          <div css={styles.narrowColumn} ref={rightColumnRef}>
            <PartnerMiniModule
              linkHref={`#${PARTNER_MODULE_ID}`}
              location={product.partner.location}
              logoSrc={product.partner.logo}
              name={product.partner.name}
              partnerRating={partnerRating || undefined}
              partnerReviewCount={partnerReviewCount}
            />
          </div>
          <div css={[styles.widerColumn, styles.spanAcrossRows]}>
            <ProductImagesCarouselContainer />
          </div>
          <div css={styles.narrowColumn}>
            <ProductPriceAndRatingWithTracking
              averageRating={reviews.averageProductRating || undefined}
              badge={
                product.attributes?.highlyRated ? 'mostLoved' : product.isNew ? 'new' : undefined
              }
              numberOfReviews={reviews.numberOfReviews || 0}
              preSalePrices={product.preSalePrices}
              price={product.price}
              priceIsMinimum={priceIsMinimum}
              reviewsLinkHref={`#${REVIEWS_URL_FRAGMENT_ID}`}
              salePercentage={product.salePercentage || 0}
            />
            {offerLabel && offerLabelAllowedForProduct && (
              <OfferLabel
                countdown={offerLabel.countdown}
                message={offerLabel.message}
                promoCode={offerLabel.promoCode}
                showInRed={offerLabel.showInRed}
              />
            )}
            {isMadeToOrder(product.inventoryStatus) && <MadeToOrder />}
          </div>
          {productFeatures.length > 0 && (
            <div css={styles.narrowColumn}>
              <ProductFeaturesWithTracking
                currency={product.price.currency}
                minimumOptionsCost={minimumOptionsCost}
                productFeatures={productFeatures}
              />
            </div>
          )}
          <div css={styles.narrowColumn}>
            {deliveryOptionsLoaded && <DeliverySummary />}
            <ProductOptionsContainer />
            <BasketCTAButtonContainer />
          </div>
        </div>
        {/* end grid containing a column that spans across rows */}

        <div css={[styles.maxWidthContainer, styles.largeSpacingBottom]}>
          <ExpandablePanel
            collapseButtonText="Read less"
            expandButtonText="Read more"
            onToggle={(expanded) =>
              sendGAEvent({
                event: 'custom_event',
                event_category: PRODUCT_DETAILS_HEADING,
                event_action: expanded ? 'Expand' : 'Collapse',
                event_label: 'not set',
              })
            }
          >
            <h2 css={styles.heading}>{PRODUCT_DETAILS_HEADING}</h2>
            <ProductDescriptionContainer />
          </ExpandablePanel>
        </div>

        <div
          css={[styles.maxWidthContainer, styles.largeSpacingBottom]}
          id={REVIEWS_URL_FRAGMENT_ID}
        >
          <div css={styles.overallRatingWrapper}>
            <ProductRatingMaxiSummary
              averageRating={reviews.averageProductRating || null}
              numberOfReviews={reviews.numberOfReviews || 0}
            />
          </div>
          <div css={styles.productReviewsWrapper}>
            <ProductReviewsContainer />
          </div>
        </div>

        <div css={[styles.maxWidthContainer, styles.largeSpacingBottom]}>
          <LazyRender
            options={{
              rootMargin: RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN,
            }}
          >
            <RecommendedProductsCarouselContainer
              servingConfig={
                product.onSale ? 'pdp_similar_products' : 'pdp-similar-products-bury-sale'
              }
              title="Similar products"
            />
          </LazyRender>
        </div>

        <div css={[styles.maxWidthContainer, styles.largeSpacingBottom]}>
          <TrustCarouselWithTracking />
        </div>

        <div css={[styles.partnerModules]} id={PARTNER_MODULE_ID}>
          <div css={[styles.maxWidthContainer, styles.largeSpacingBottom]}>
            <PartnerMaxiModuleWithTracking
              location={product.partner.location}
              logoSrc={product.partner.logo}
              name={product.partner.name}
              onContactPartnerClick={openContactPartnerModal}
              partnerLinkHref={partnerHref}
              partnerRating={partnerRating || undefined}
              partnerReviewCount={partnerReviewCount}
              partnerShortcode={product.partner.shortcode}
            />
            <div css={[styles.partnerProductsCarousel]}>
              <LazyRender
                options={{
                  rootMargin: RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN,
                }}
              >
                <RecommendedProductsCarouselContainer
                  servingConfig="pdp_same_seller_2"
                  title="Our bestsellers"
                />
              </LazyRender>
            </div>
          </div>
        </div>
        <div css={[styles.maxWidthContainer, styles.largeSpacingBottom]}>
          <MembershipBannerSection />
        </div>
        {product.publicAttributes && (
          <div css={[styles.maxWidthContainer, styles.largeSpacingBottom]}>
            <PublicAttributes
              publicAttributes={adaptPublicAttributesToUI(product.publicAttributes)}
            />
          </div>
        )}
        <div css={[styles.maxWidthContainer, styles.largeSpacingBottom]}>
          <LinkCarouselContainer />
        </div>
        <StickySection triggerElementId={MAIN_PAGE_CTA_ID}>
          <BasketCTAButtonContainer />
        </StickySection>
      </div>
      <ProductImagesModalContainer />
      <DeliveryDrawer target={rightColumnRef} />
      {options.length > 0 && <ProductOptionsDrawerContainer target={rightColumnRef} />}
      <BasketErrorNotification />
    </>
  );
};
